// Margin against monthly calls: the one chart here whose x axis is not months.
//
// It answers the question the calculator exists for — does this price hold up
// if they use a little, or a lot? — so it marks the two volumes that matter:
// the scenario's own, and where a prepaid block runs out, which is
// the kink between "every call only costs" and "every call also bills".
//
// Drawn in the manner of MonthlyChart: measured width, hairline grid, one
// axis, a crosshair with a readout, and a table of the same figures for a
// reader who cannot see the line.

import { useCallback, useId, useRef, useState, type ReactNode } from "react";
import type { CurvePoint } from "./pricingModel.js";
import styles from "./PricingPage.module.css";

export interface VolumeCurveProps {
  points: readonly CurvePoint[];
  currentCalls: number;
  prepaidCalls: number | null;
  formatCalls: (calls: number) => string;
  formatContribution: (minor: number) => string;
}

const HEIGHT = 220;
const MARGIN = { top: 14, right: 16, bottom: 30, left: 52 } as const;
const FALLBACK_WIDTH = 640;
const MIN_WIDTH = 320;
/** Margins below this are drawn at it: a loss is a loss, and a -900% would flatten the rest. */
const FLOOR_PCT = -100;

export function VolumeCurve({
  points,
  currentCalls,
  prepaidCalls,
  formatCalls,
  formatContribution,
}: VolumeCurveProps): ReactNode {
  const titleId = useId();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [width, setWidth] = useState<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const measure = useCallback((node: HTMLDivElement | null) => {
    if (node === null) return;
    setWidth(node.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.width;
      if (measured !== undefined) setWidth(measured);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const known = points.filter((point) => point.marginPct !== null);
  if (known.length < 2) {
    return <div className={styles.muted}>No margin to draw: nothing reaches us at any volume.</div>;
  }

  const frame = Math.max(width ?? FALLBACK_WIDTH, MIN_WIDTH);
  const plotWidth = frame - MARGIN.left - MARGIN.right;
  const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
  const maxCalls = points.at(-1)?.calls ?? 1;
  const lowest = Math.min(0, ...known.map((point) => point.marginPct!));
  const yMin = Math.max(FLOOR_PCT, Math.floor(lowest / 25) * 25);
  const yMax = 100;
  const x = (calls: number): number => MARGIN.left + (calls / maxCalls) * plotWidth;
  const y = (pct: number): number =>
    MARGIN.top + plotHeight - ((Math.max(pct, yMin) - yMin) / (yMax - yMin)) * plotHeight;
  const ticks: number[] = [];
  for (let tick = yMin; tick <= yMax; tick += 25) ticks.push(tick);

  const path = known.map((point) => `${x(point.calls)},${y(point.marginPct!)}`).join(" ");
  const hovered = hover === null ? null : points[hover];

  const onPointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (svg === null) return;
    const rect = svg.getBoundingClientRect();
    const userX = ((event.clientX - rect.left) / rect.width) * frame;
    const calls = ((userX - MARGIN.left) / plotWidth) * maxCalls;
    let nearest = 0;
    points.forEach((point, index) => {
      if (Math.abs(point.calls - calls) < Math.abs(points[nearest]!.calls - calls)) nearest = index;
    });
    setHover(nearest);
  };

  return (
    <div className={styles.curve} ref={measure}>
      <svg
        ref={svgRef}
        className={styles.curveSvg}
        viewBox={`0 0 ${frame} ${HEIGHT}`}
        role="img"
        aria-labelledby={titleId}
        onPointerMove={onPointerMove}
        onPointerLeave={() => setHover(null)}
      >
        <title id={titleId}>
          Margin at full volume by calls a month. The same figures are in the table below.
        </title>
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              className={tick === 0 ? styles.curveBaseline : styles.curveGrid}
              x1={MARGIN.left}
              x2={frame - MARGIN.right}
              y1={y(tick)}
              y2={y(tick)}
            />
            <text
              className={styles.curveTick}
              x={MARGIN.left - 8}
              y={y(tick)}
              textAnchor="end"
              dominantBaseline="middle"
            >
              {tick}%
            </text>
          </g>
        ))}
        {[0, 0.25, 0.5, 0.75, 1].map((share) => (
          <text
            key={share}
            className={styles.curveTick}
            x={x(maxCalls * share)}
            y={HEIGHT - MARGIN.bottom + 16}
            textAnchor={share === 0 ? "start" : share === 1 ? "end" : "middle"}
          >
            {formatCalls(maxCalls * share)}
          </text>
        ))}
        {prepaidCalls !== null && prepaidCalls <= maxCalls && (
          <g>
            <line
              className={styles.curveMarkAllowance}
              x1={x(prepaidCalls)}
              x2={x(prepaidCalls)}
              y1={MARGIN.top}
              y2={MARGIN.top + plotHeight}
            />
            <text className={styles.curveMarkText} x={x(prepaidCalls) + 4} y={MARGIN.top + 10}>
              prepaid used up
            </text>
          </g>
        )}
        <line
          className={styles.curveMarkCurrent}
          x1={x(currentCalls)}
          x2={x(currentCalls)}
          y1={MARGIN.top}
          y2={MARGIN.top + plotHeight}
        />
        <text
          className={styles.curveMarkText}
          x={x(currentCalls) + 4}
          y={MARGIN.top + plotHeight - 6}
        >
          this scenario
        </text>
        <polyline className={styles.curveLine} points={path} />
        {hovered !== null && hovered !== undefined && hovered.marginPct !== null && (
          <g>
            <line
              className={styles.curveCrosshair}
              x1={x(hovered.calls)}
              x2={x(hovered.calls)}
              y1={MARGIN.top}
              y2={MARGIN.top + plotHeight}
            />
            <circle
              className={styles.curveDot}
              cx={x(hovered.calls)}
              cy={y(hovered.marginPct)}
              r={4}
            />
          </g>
        )}
      </svg>
      <div className={styles.curveReadout} aria-live="polite">
        {hovered !== null && hovered !== undefined
          ? `${formatCalls(hovered.calls)} calls a month · ${
              hovered.marginPct === null ? "no margin" : `${hovered.marginPct.toFixed(1)}% margin`
            } · ${formatContribution(hovered.contributionMinor)} contribution`
          : "Hover the line to read a volume."}
      </div>
      <div className="srOnly">
        <table>
          <caption>Margin by calls a month</caption>
          <thead>
            <tr>
              <th scope="col">Calls a month</th>
              <th scope="col">Margin</th>
              <th scope="col">Contribution</th>
            </tr>
          </thead>
          <tbody>
            {points.map((point) => (
              <tr key={point.calls}>
                <td>{formatCalls(point.calls)}</td>
                <td>{point.marginPct === null ? "none" : `${point.marginPct.toFixed(1)}%`}</td>
                <td>{formatContribution(point.contributionMinor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
